/**
 * useAutoResizeTextarea — grows a composer to fit what has been typed, up to a
 * ceiling, then scrolls (mobile chatInputToolbar / comment composer parity).
 *
 * The height is reset to `auto` before every measurement: `scrollHeight` never
 * shrinks below the element's current height, so without the reset the box
 * could only ever grow, and deleting a line would leave the gap behind.
 */
import { useCallback, useLayoutEffect, type RefObject } from 'react';

export interface UseAutoResizeTextareaOptions {
  /** Tallest the composer may grow before it starts scrolling, in px. */
  maxHeight: number;
}

export const useAutoResizeTextarea = (
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  { maxHeight }: UseAutoResizeTextareaOptions,
): void => {
  const resize = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    const next = Math.min(node.scrollHeight, maxHeight);
    node.style.height = `${next}px`;
    node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [ref, maxHeight]);

  // Layout effect so the height is committed in the same frame as the text,
  // otherwise the composer visibly jumps a row behind what is being typed.
  useLayoutEffect(resize, [resize, value]);
};
